import React, { useCallback, useState } from 'react';
import { Bot, BriefcaseBusiness, Info, MapPin, Phone } from 'lucide-react';
import { ChatInfoTab } from './ChatInfoTab';
import { ChatDealsTab } from './ChatDealsTab';
import { ChatAiTab } from './ChatAiTab';
import { DealFormModal } from './DealFormModal';
import '../../styles/chats.css';

/**
 * Client card shell: header, tabs, and the three panels.
 *
 * This file used to be 654 lines with one named function and 28 inline handlers,
 * so nothing in it could be rendered or asserted on in isolation. The panels now
 * live in ChatInfoTab / ChatDealsTab / ChatAiTab and the deal dialog in
 * DealFormModal; what remains here is composition.
 */

interface ChatDetailsSidebarProps {
  activeChat: any;
  chatTags: string[];
  activeChatDeals: any[];
  isDealFormOpen: boolean;
  setIsDealFormOpen: (v: boolean) => void;
  resetDealForm: () => void;
  onUpdateChatField: (id: number, fields: Record<string, unknown>) => void;
  onEditDealClick: (deal: any) => void;
  onUpdateDealStatus: (dealId: number, status: 'won' | 'lost') => void;
  handleSaveDeal: (e: React.FormEvent) => void;
  dealTitle: string;
  setDealTitle: (v: string) => void;
  dealAmount: string;
  setDealAmount: (v: string) => void;
  dealDiscountPercent?: number;
  setDealDiscountPercent?: (v: number) => void;
  dealStage?: string;
  setDealStage?: (v: string) => void;
  dealItems: any[];
  editingDealId: number | null;
  selectedProdId: string;
  setSelectedProdId: (v: string) => void;
  catalog: any[];
  onAddItemToDeal: () => void;
  onRemoveItemFromDeal: (prodId: number) => void;
  onUpdateItemQty: (prodId: number, qty: number) => void;
  autopilot: boolean;
  setAutopilot: (v: boolean) => void;
  channels: Record<string, any>;
  avatarColor: (name: string) => string;
  initials: (name: string) => string;
}

type TabId = 'info' | 'deals' | 'ai';

export const ChatDetailsSidebar: React.FC<ChatDetailsSidebarProps> = ({
  activeChat,
  chatTags,
  activeChatDeals,
  isDealFormOpen,
  setIsDealFormOpen,
  resetDealForm,
  onUpdateChatField,
  onEditDealClick,
  onUpdateDealStatus,
  handleSaveDeal,
  dealTitle,
  setDealTitle,
  dealAmount,
  setDealAmount,
  dealDiscountPercent,
  setDealDiscountPercent,
  dealStage,
  setDealStage,
  dealItems,
  editingDealId,
  selectedProdId,
  setSelectedProdId,
  catalog,
  onAddItemToDeal,
  onRemoveItemFromDeal,
  onUpdateItemQty,
  autopilot,
  setAutopilot,
  channels,
  avatarColor,
  initials,
}) => {
  const [activeTab, setActiveTab] = useState<TabId>('info');
  const ch = channels[activeChat.channelType || activeChat.channel?.type] || channels.widget;

  const tabs = [
    { id: 'info' as TabId, label: 'Инфо', icon: Info },
    { id: 'deals' as TabId, label: `Сделки (${activeChatDeals.length})`, icon: BriefcaseBusiness },
    { id: 'ai' as TabId, label: 'ИИ', icon: Bot },
  ];

  const handleTabKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const lastIndex = tabs.length - 1;
    let nextIndex = index;
    if (event.key === 'ArrowRight') nextIndex = index === lastIndex ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') nextIndex = index === 0 ? lastIndex : index - 1;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = lastIndex;
    else return;
    event.preventDefault();
    setActiveTab(tabs[nextIndex].id);
    event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[nextIndex]?.focus();
  }, [tabs.length]);

  const openNewDeal = useCallback(() => {
    resetDealForm();
    setIsDealFormOpen(true);
  }, [resetDealForm, setIsDealFormOpen]);

  const closeDealForm = useCallback(() => setIsDealFormOpen(false), [setIsDealFormOpen]);

  return (
    <div className="chat-side">
      <div className="client-hero-card">
        <div className="side-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <span>Карточка клиента</span>
          <span className="client-id-pill">ID #{activeChat.id}</span>
        </div>

        <div className="client-hero-header" style={{ marginBottom: '14px' }}>
          <div className="client-avatar-wrapper">
            <div className="client-avatar-lg" style={{ background: avatarColor(activeChat.contactName) }}>
              {initials(activeChat.contactName)}
            </div>
            <div
              className="client-channel-badge"
              style={{ background: ch.color || 'var(--primary)' }}
              dangerouslySetInnerHTML={{ __html: ch.svg }}
              title={ch.name}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="client-name-title client-name-ellipsis" title={activeChat.contactName}>
              {activeChat.contactName || 'Без имени'}
            </div>
            <div className="client-sub-info"><Phone size={12} /><span>{activeChat.contactPhone || 'Телефон не указан'}</span></div>
            {activeChat.contactCity && (
              <div className="client-sub-info" style={{ marginTop: '2px' }}><MapPin size={12} /><span>{activeChat.contactCity}</span></div>
            )}
          </div>
        </div>

        <div className="client-tabs" role="tablist" aria-label="Разделы карточки клиента">
          {tabs.map((tab, index) => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                id={`client-tab-${tab.id}`}
                type="button"
                role="tab"
                className={`client-tab ${activeTab === tab.id ? 'active' : ''}`}
                aria-selected={activeTab === tab.id}
                aria-controls={`client-panel-${tab.id}`}
                tabIndex={activeTab === tab.id ? 0 : -1}
                onClick={() => setActiveTab(tab.id)}
                onKeyDown={event => handleTabKeyDown(event, index)}
              >
                <TabIcon size={14} aria-hidden="true" /><span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {activeTab === 'info' && (
        <ChatInfoTab activeChat={activeChat} chatTags={chatTags} onUpdateChatField={onUpdateChatField} />
      )}

      {activeTab === 'deals' && (
        <ChatDealsTab
          activeChatDeals={activeChatDeals}
          onOpenNewDeal={openNewDeal}
          onEditDealClick={onEditDealClick}
          onUpdateDealStatus={onUpdateDealStatus}
        />
      )}

      {activeTab === 'ai' && (
        <ChatAiTab
          conversationId={activeChat.id}
          aiEnabled={!!activeChat.aiEnabled}
          autopilot={autopilot}
          setAutopilot={setAutopilot}
        />
      )}

      {isDealFormOpen && (
        <DealFormModal
          editingDealId={editingDealId}
          onClose={closeDealForm}
          onSubmit={handleSaveDeal}
          dealTitle={dealTitle}
          setDealTitle={setDealTitle}
          dealAmount={dealAmount}
          setDealAmount={setDealAmount}
          dealDiscountPercent={dealDiscountPercent}
          setDealDiscountPercent={setDealDiscountPercent}
          dealStage={dealStage}
          setDealStage={setDealStage}
          dealItems={dealItems}
          selectedProdId={selectedProdId}
          setSelectedProdId={setSelectedProdId}
          catalog={catalog}
          onAddItemToDeal={onAddItemToDeal}
          onRemoveItemFromDeal={onRemoveItemFromDeal}
          onUpdateItemQty={onUpdateItemQty}
        />
      )}
    </div>
  );
};
